import { lazy } from 'react';

/**
 * Wraps React.lazy so that a failed dynamic import (typically a stale
 * chunk after a new deploy: "ChunkLoadError" / "Unexpected token '<'")
 * triggers a single hard page reload to fetch the fresh index.html and
 * the new chunk hashes.
 *
 * The reload is guarded by sessionStorage so we never end up in an
 * infinite reload loop if the chunk is genuinely missing (e.g. a real
 * build/deploy problem) rather than just stale.
 */
const lazyWithRetry = (componentImport) =>
  lazy(async () => {
    const STORAGE_KEY = 'chunk-reload-attempted';

    try {
      const component = await componentImport();
      // Successful load — clear the guard so future stale chunks can reload.
      window.sessionStorage.removeItem(STORAGE_KEY);
      return component;
    } catch (error) {
      const alreadyReloaded =
        window.sessionStorage.getItem(STORAGE_KEY) === 'true';

      if (!alreadyReloaded) {
        // First failure: assume a stale chunk from a previous deploy and
        // reload once to pick up the latest index.html + chunk hashes.
        window.sessionStorage.setItem(STORAGE_KEY, 'true');
        window.location.reload();
        // Return a never-resolving promise so React keeps the Suspense
        // fallback until the reload takes over.
        return new Promise(() => {});
      }

      // We already reloaded and it still failed — this is a real error.
      // Re-throw so the ErrorBoundary can show the fallback UI.
      throw error;
    }
  });

export default lazyWithRetry;
